<template>
    <div>
        <div class="d-flex align-center flex-wrap px-3 pt-2">
            <v-btn small text class="mr-1" @click="$emit('voltar')">
                <v-icon left small>mdi-arrow-left</v-icon>
                Frota
            </v-btn>

            <v-chip small label class="mr-2">
                <v-icon left x-small>{{ icone }}</v-icon>
                {{ nomeDoServidor }}
            </v-chip>

            <span class="text-caption text--secondary">
                {{ textoUltimaColeta }}
            </span>

            <v-spacer/>

            <v-btn-toggle
                v-model="periodo"
                mandatory
                dense
                borderless
                class="mr-2"
                @change="carregarSeries"
            >
                <v-btn v-for="p in periodos" :key="p.chave" :value="p.chave" small>
                    {{ p.chave }}
                </v-btn>
            </v-btn-toggle>

            <v-btn icon :loading="loading" @click="carregar">
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </div>

        <div class="px-3 pt-2">
            <v-alert
                v-for="alerta in alertasAbertos"
                :key="alerta.id"
                dense
                text
                border="left"
                class="mb-2"
                :type="alerta.severidade === 'critico' ? 'error' : 'warning'"
            >
                <span class="font-weight-medium">{{ alerta.regra }}</span>
                — {{ formatarValor(alerta.metrica, alerta.valor) }}
                (limite {{ formatarValor(alerta.metrica, alerta.limite) }})
                <span class="text-caption">· desde {{ formatDate(alerta.disparado_em, 'DD/MM HH:mm') }}</span>
            </v-alert>

            <v-alert v-if="semColeta" dense text border="left" type="error" class="mb-2">
                <span class="font-weight-medium">Sem coleta há mais de {{ limiteSemColeta }} minutos.</span>
                O coletor pode ter parado — os números abaixo são os últimos conhecidos,
                não o estado de agora.
            </v-alert>

            <v-alert v-if="errosDeColeta.length" dense text border="left" type="warning" class="mb-2">
                <span class="font-weight-medium">Parte da coleta falhou:</span>
                {{ errosDeColeta.join(' · ') }}
            </v-alert>
        </div>

        <v-row dense class="px-2">
            <v-col
                v-for="card in cards"
                :key="card.label"
                cols="6"
                sm="4"
                md="3"
            >
                <CardMetrica v-bind="card"/>
            </v-col>
        </v-row>

        <v-row dense class="px-2 pb-4">
            <v-col v-for="g in graficos" :key="g.titulo" cols="12" md="6">
                <v-card elevation="2">
                    <v-card-subtitle class="pb-0 font-weight-medium">
                        {{ g.titulo }}
                    </v-card-subtitle>
                    <v-card-text class="pt-1">
                        <LineChart
                            :labels="labels"
                            :datasets="g.datasets"
                            :unidade="g.unidade"
                            :stacked="!!g.stacked"
                            :area="!!g.area"
                            :max="g.max || null"
                            :height="200"
                        />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart';
import CardMetrica from './CardMetrica';
import { carregarCatalogo, formatarValor } from './catalogo';
import { iconeDoPapel } from './servidores';

const PERIODOS = [
    { chave: '1h' },
    { chave: '6h' },
    { chave: '24h' },
    { chave: '7d' },
    { chave: '30d' },
    { chave: '12m' },
];

const AZUL = 'rgba(25, 118, 210, 1)';
const VERDE = 'rgba(76, 175, 80, 1)';
const LARANJA = 'rgba(255, 152, 0, 1)';
const ROXO = 'rgba(156, 39, 176, 1)';
const VERMELHO = 'rgba(244, 67, 54, 1)';

export default {
    name: 'TabVisaoGeral',

    components: {
        LineChart,
        CardMetrica,
    },

    props: {
        host: { type: String, required: true },
    },

    data: () => ({
        periodos: PERIODOS,
        periodo: '6h',
        loading: false,
        atual: { metricas: {}, faixas: {} },
        serie: { pontos: [], resolucao: 'minuto' },
        alertas: [],
        timer: null,
    }),

    mounted() {
        this.carregar();
        this.timer = setInterval(() => this.carregar(), 60000);
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    computed: {
        m() {
            return this.atual.metricas || {};
        },

        f() {
            return this.atual.faixas || {};
        },

        nomeDoServidor() {
            return (this.atual.servidor && this.atual.servidor.nome) || this.host;
        },

        icone() {
            return iconeDoPapel(this.atual.servidor && this.atual.servidor.papel);
        },

        errosDeColeta() {
            const erros = this.atual.erros_coleta;

            if (!erros) return [];

            return Object.keys(erros).map(grupo => `${grupo}: ${erros[grupo]}`);
        },

        alertasAbertos() {
            return this.alertas.filter(a => !a.resolvido_em);
        },

        limiteSemColeta() {
            return (this.atual.servidor && this.atual.servidor.heartbeat_minutos) || 10;
        },

        semColeta() {
            if (!this.atual.coletado_em) return false;
            return this.moment().diff(this.moment(this.atual.coletado_em), 'minutes') >= this.limiteSemColeta;
        },

        textoUltimaColeta() {
            if (!this.atual.coletado_em) return '';
            return `última coleta ${this.moment(this.atual.coletado_em).format('HH:mm')}`;
        },

        labels() {
            const formato = {
                minuto: 'HH:mm',
                hora: 'DD/MM HH:mm',
                dia: 'DD/MM',
            }[this.serie.resolucao] || 'HH:mm';

            return this.serie.pontos.map(p => this.moment(p.t).format(formato));
        },

        cards() {
            const m = this.m;
            const gb = mb => (mb / 1024).toFixed(1);

            return [
                {
                    label: 'CPU',
                    valor: formatarValor('cpu_percent', m.cpu_percent),
                    sub: `${this.atual.cpu_cores || '—'} núcleos · iowait ${formatarValor('cpu_iowait', m.cpu_iowait)}`,
                    icone: 'mdi-chip',
                    faixa: this.f.cpu_percent || 'ok',
                    progresso: m.cpu_percent || 0,
                },
                {
                    label: 'Carga por núcleo',
                    valor: formatarValor('load_per_core', m.load_per_core),
                    sub: 'acima de 1 já significa fila',
                    icone: 'mdi-speedometer',
                    faixa: this.f.load_per_core || 'ok',
                    progresso: Math.min(100, (m.load_per_core || 0) * 50),
                },
                {
                    label: 'Memória',
                    valor: formatarValor('mem_used_percent', m.mem_used_percent),
                    sub: `${gb(m.mem_available_mb || 0)} GB disponíveis de ${gb(this.atual.mem_total_mb || 0)} GB`,
                    icone: 'mdi-memory',
                    faixa: this.f.mem_used_percent || 'ok',
                    progresso: m.mem_used_percent || 0,
                },
                {
                    label: 'Swap',
                    valor: formatarValor('swap_used_mb', m.swap_used_mb),
                    sub: `saída ${formatarValor('swap_out_kbs', m.swap_out_kbs)}`,
                    icone: 'mdi-swap-vertical',
                    faixa: this.f.swap_out_kbs || 'ok',
                    progresso: null,
                },
                {
                    label: 'Disco',
                    valor: formatarValor('disk_used_percent', m.disk_used_percent),
                    sub: `${formatarValor('disk_free_gb', m.disk_free_gb)} livres de ${this.atual.disk_total_gb} GB`,
                    icone: 'mdi-harddisk',
                    faixa: this.f.disk_used_percent || 'ok',
                    progresso: m.disk_used_percent || 0,
                },
                {
                    label: 'Conexões Postgres',
                    valor: formatarValor('pg_connections', m.pg_connections),
                    sub: `de ${m.pg_max_connections || '—'} · cache hit ${formatarValor('pg_cache_hit_ratio', m.pg_cache_hit_ratio)}`,
                    icone: 'mdi-database',
                    faixa: this.f.pg_conn_percent || 'ok',
                    progresso: m.pg_conn_percent || 0,
                },
                {
                    label: 'Fila',
                    valor: formatarValor('queue_pending', m.queue_pending),
                    sub: `${formatarValor('queue_stuck', m.queue_stuck)} presos · ${formatarValor('queue_failed_24h', m.queue_failed_24h)} falhos em 24h`,
                    icone: 'mdi-tray-full',
                    faixa: this.f.queue_stuck || 'ok',
                    progresso: null,
                },
                {
                    label: 'No ar há',
                    valor: this.uptime,
                    sub: this.atual.services_down && this.atual.services_down.length
                        ? `fora do ar: ${this.atual.services_down.join(', ')}`
                        : 'todos os serviços respondendo',
                    icone: 'mdi-server',
                    faixa: this.atual.services_down && this.atual.services_down.length ? 'critico' : 'ok',
                    progresso: null,
                },
            ];
        },

        uptime() {
            const s = this.atual.uptime_seconds || 0;
            const dias = Math.floor(s / 86400);
            const horas = Math.floor((s % 86400) / 3600);
            return dias ? `${dias}d ${horas}h` : `${horas}h`;
        },

        graficos() {
            const s = c => this.serie.pontos.map(p => p[c]);
            const maximo = this.m.pg_max_connections || 200;

            return [
                {
                    titulo: 'CPU (%)',
                    unidade: '%',
                    stacked: true,
                    area: true,
                    max: 100,
                    datasets: [
                        { label: 'Usuário', data: s('cpu_user'), color: AZUL },
                        { label: 'Sistema', data: s('cpu_system'), color: ROXO },
                        { label: 'Espera de disco', data: s('cpu_iowait'), color: LARANJA },
                    ],
                },
                {
                    titulo: 'Carga por núcleo',
                    unidade: '',
                    datasets: [
                        { label: 'Carga / núcleo', data: s('load_per_core'), color: AZUL, fill: true },
                    ],
                },
                {
                    titulo: 'Memória usada (%)',
                    unidade: '%',
                    max: 100,
                    datasets: [
                        { label: 'Memória', data: s('mem_used_percent'), color: VERDE, fill: true },
                    ],
                },
                {
                    titulo: 'Swap',
                    unidade: 'MB',
                    datasets: [
                        { label: 'Em uso (MB)', data: s('swap_used_mb'), color: VERMELHO, fill: true },
                    ],
                },
                {
                    titulo: 'Disco usado (%)',
                    unidade: '%',
                    max: 100,
                    datasets: [
                        { label: 'Espaço', data: s('disk_used_percent'), color: AZUL },
                        { label: 'Inodes', data: s('inodes_used_percent'), color: LARANJA },
                    ],
                },
                {
                    titulo: 'I/O de disco (KB/s)',
                    unidade: 'KB/s',
                    datasets: [
                        { label: 'Leitura', data: s('disk_read_kbs'), color: AZUL },
                        { label: 'Escrita', data: s('disk_write_kbs'), color: LARANJA },
                    ],
                },
                {
                    titulo: 'Rede (KB/s)',
                    unidade: 'KB/s',
                    datasets: [
                        { label: 'Recebido', data: s('net_rx_kbs'), color: VERDE },
                        { label: 'Enviado', data: s('net_tx_kbs'), color: AZUL },
                    ],
                },
                {
                    titulo: 'Conexões no Postgres',
                    unidade: '',
                    datasets: [
                        { label: 'Conexões', data: s('pg_connections'), color: ROXO, fill: true },
                        {
                            label: `Máximo (${maximo})`,
                            data: this.serie.pontos.map(() => maximo),
                            color: VERMELHO,
                            tracejada: true,
                            fill: false,
                        },
                    ],
                },
                {
                    titulo: 'Fila de jobs',
                    unidade: '',
                    datasets: [
                        { label: 'Pendentes', data: s('queue_pending'), color: AZUL },
                        { label: 'Presos (+30s)', data: s('queue_stuck'), color: VERMELHO },
                    ],
                },
            ];
        },
    },

    methods: {
        formatarValor,

        async carregar() {
            this.loading = true;
            try {
                // O catálogo precisa chegar antes dos dados: é dele que saem os
                // rótulos e as unidades usadas na formatação de tudo abaixo.
                await carregarCatalogo(this.$http);

                const [atual, serie, alertas] = await Promise.all([
                    this.$http.get('monitoramento/atual', { params: { host: this.host } }),
                    this.$http.get('monitoramento/series', { params: { periodo: this.periodo, host: this.host } }),
                    this.$http.get('monitoramento/alertas', { params: { status: 'abertos', host: this.host } }),
                ]);

                this.atual = atual.data;
                this.serie = serie.data;
                this.alertas = alertas.data;
            } catch (e) {
                this.notify('Não foi possível carregar as métricas', 'error');
            }
            this.loading = false;
        },

        async carregarSeries() {
            this.loading = true;
            try {
                const { data } = await this.$http.get('monitoramento/series', {
                    params: { periodo: this.periodo, host: this.host },
                });

                this.serie = data;
            } catch (e) {
                this.notify('Não foi possível carregar o período', 'error');
            }
            this.loading = false;
        },
    },
};
</script>
